import { Group, Stack, Text } from '@mantine/core'
import { NumberInput } from '../Common/NumberInput'
import { Select } from '../Common/Select'
import { Textarea } from '../Common/Textarea'
import { TextInput } from '../Common/TextInput'
import type { TimeEntryFormValues } from '../../types/timeEntry'

// Replicon's limits on a description. The maximum is also enforced by the textarea itself,
// which truncates a longer paste; both are checked again before a save.
export const DESCRIPTION_MIN = 5
export const DESCRIPTION_MAX = 300

// A dropdown and the state of whatever fetch fills it.
export interface TimeEntrySelectField {
  options: { value: string; label: string }[]
  loading?: boolean
  // Why the dropdown can't be used yet — e.g. Labor Code when the claim has no work package.
  disabled?: boolean
  placeholder?: string
  // Lets the user type to filter a long list. The text matches the whole label, code included.
  searchable?: boolean
  // Marks the field required — the backend rejects a time entry without it.
  required?: boolean
  // Load or validation failure, shown under the dropdown.
  error?: string
}

export interface TimeEntryFormErrors {
  timeWorked?: string
  date?: string
  laborCode?: string
  billingRole?: string
  description?: string
}

export interface TimeEntryFormProps {
  values: TimeEntryFormValues
  // Shown, not chosen: time is always booked against the claim's first work package. The form only
  // appears once they have loaded, or failed to — `name` is null in the second case, beside `error`.
  workPackage: { name: string | null; error?: string }
  laborCode: TimeEntrySelectField
  billingRole: TimeEntrySelectField
  billingStatus: TimeEntrySelectField
  // Latest date that can be booked, yyyy-mm-dd — time is never entered ahead of the day
  // it was worked.
  maxDate: string
  // Earliest date that can be booked, yyyy-mm-dd.
  minDate: string
  onChange: <K extends keyof TimeEntryFormValues>(field: K, value: TimeEntryFormValues[K]) => void
  errors?: TimeEntryFormErrors
}

// Mantine renders its error text with no role, so an error that appears while the user is
// typing is never announced. Wrapping it makes each one an alert; the wrapper still sits
// inside the element Mantine points aria-describedby at, and still sets aria-invalid.
function announce(message?: string) {
  return message ? <span role="alert">{message}</span> : undefined
}

interface LookupSelectProps {
  label: string
  field: TimeEntrySelectField
  value: string
  onChange: (value: string) => void
}

// Mantine only clears the text a Select shows when its value is null — an empty string
// leaves the previous label on screen, so a field cleared in code would still look set.
function LookupSelect({ label, field, value, onChange }: Readonly<LookupSelectProps>) {
  return (
    <Select
      size="xs"
      label={label}
      withAsterisk={field.required}
      data={field.options}
      value={value === '' ? null : value}
      placeholder={field.loading ? 'Loading…' : field.placeholder}
      disabled={field.disabled || field.loading}
      searchable={field.searchable}
      nothingFoundMessage={field.searchable ? 'No matches' : undefined}
      error={announce(field.error)}
      onChange={(next) => onChange(next ?? '')}
    />
  )
}

// A label over plain text rather than a field, because there is nothing to choose. The ids tie
// the label to the value for screen readers.
function WorkPackageText({ name, error }: Readonly<TimeEntryFormProps['workPackage']>) {
  let value = <Text fz="xs">{name}</Text>
  if (error)
    value = (
      <Text fz="xs" c="var(--mantine-color-error)" role="alert">
        {error}
      </Text>
    )

  return (
    <Stack gap={2} role="group" aria-labelledby="time-entry-work-package">
      <Text id="time-entry-work-package" fz="xs" fw={500}>
        Work Package
      </Text>
      {value}
    </Stack>
  )
}

export function TimeEntryForm({
  values,
  workPackage,
  laborCode,
  billingRole,
  billingStatus,
  maxDate,
  minDate,
  onChange,
  errors,
}: Readonly<TimeEntryFormProps>) {
  return (
    <Stack gap="sm">
      <WorkPackageText {...workPackage} />

      <Group gap="xs" align="flex-start" grow wrap="nowrap">
        <NumberInput
          withAsterisk
          size="xs"
          label="Hours (e.g., 1.5)"
          placeholder="1.5"
          value={values.timeWorked}
          onChange={(value) => onChange('timeWorked', value)}
          min={0}
          // Hours are entered as a decimal to one place (6 minutes); a second digit can't be typed
          // or pasted.
          decimalScale={1}
          // No up/down arrows, and the Up/Down keys don't step it either: hours are typed.
          hideControls
          withKeyboardEvents={false}
          error={announce(errors?.timeWorked)}
        />
        <TextInput
          withAsterisk
          size="xs"
          label="Date Worked"
          type="date"
          min={minDate}
          max={maxDate}
          value={values.date}
          onChange={(event) => onChange('date', event.currentTarget.value)}
          error={announce(errors?.date)}
        />
      </Group>

      <Group gap="xs" align="flex-start" grow wrap="nowrap">
        <LookupSelect
          label="Labor Code"
          field={laborCode}
          value={values.laborCode}
          onChange={(value) => onChange('laborCode', value)}
        />
        <LookupSelect
          label="Billing Role"
          field={billingRole}
          value={values.billingRole}
          onChange={(value) => onChange('billingRole', value)}
        />
      </Group>

      <LookupSelect
        label="Billing Status"
        field={billingStatus}
        value={values.billingStatus}
        onChange={(value) => onChange('billingStatus', value)}
      />

      <Stack gap={4}>
        <Textarea
          withAsterisk
          size="xs"
          label="Description"
          placeholder="e.g. Reviewed the loss adjuster report and called the insured to confirm scope"
          value={values.description}
          onChange={(event) => onChange('description', event.currentTarget.value)}
          error={announce(errors?.description)}
          maxLength={DESCRIPTION_MAX}
          autosize
          minRows={5}
          // Grows with the text, then scrolls — a long note must not push Save off screen.
          maxRows={10}
        />
        {/* Not a live region: it changes on every keystroke, which would drown out the field. */}
        <Text fz="xs" c="text.2" ta="right">
          {values.description.length}/{DESCRIPTION_MAX}
        </Text>
      </Stack>
    </Stack>
  )
}
